import type { TurnstileInstance } from "@marsidev/react-turnstile"
import type React from "react"
import { createContext, useMemo, useRef, useState } from "react"

import { useStableFunction } from "@repo/ui/hooks/use-stable-function"

export type CaptchaContextValue = {
  enabled: boolean
  token?: string
  setToken: (token: string) => void
  instance: TurnstileInstance | null
  setInstance: (ref: TurnstileInstance) => void
}

export const CaptchaContext = createContext<CaptchaContextValue>({
  enabled: false,
  token: "",
  instance: null,
  setToken: (_: string) => {
    // do nothing
    return ""
  },
  setInstance: () => {
    // do nothing
  },
})

export type CaptchaProviderProps = React.PropsWithChildren<{
  enabled: boolean
}>

export function CaptchaProvider({ enabled, children }: CaptchaProviderProps) {
  const [token, setToken] = useState<string | undefined>(undefined)
  const instanceRef = useRef<TurnstileInstance | null>(null)

  const setInstance = useStableFunction((ref: TurnstileInstance) => {
    instanceRef.current = ref
  })

  const value: CaptchaContextValue = useMemo(
    () => ({
      enabled,
      token,
      setToken,
      instance: instanceRef.current,
      setInstance,
    }),
    [enabled, token, setInstance],
  )

  return (
    <CaptchaContext.Provider value={value}>{children}</CaptchaContext.Provider>
  )
}
